import React, { useCallback, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import DataGrid, { Column, Scrolling, SearchPanel, Editing } from 'devextreme-react/data-grid';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';

export function CompetitionResults({ competitionId, data, stats, onImportResults, accessToken, onEntityChange, dataSource }) {
  const [isSaving, setIsSaving] = useState(false);

  const storeItems = dataSource?.store?._array || data?.results || [];

  const mergedStats = useMemo(() => {
    if (data) {
      return {
        heats: data.heats?.length || 0,
        finished: data.heats?.filter(h => h.status === 'finished' || h.status === 'completed')?.length || 0,
        records: data.records?.length || 0,
        relays: data.athletes?.filter(a => a.isRelay)?.length || 0,
        ...stats
      };
    }
    return {
      heats: 0,
      finished: 0,
      records: 0,
      relays: 0,
      ...stats
    };
  }, [data, stats]);

  const heatLookup = useMemo(() => (
    data?.heats?.map((heat) => ({ id: heat.heatId, name: `Lauf ${heat.number || ''}`.trim() })) || []
  ), [data]);

  const athleteLookup = useMemo(() => (
    data?.athletes?.map((athlete) => ({ id: athlete.athleteId, name: `${athlete.firstname || ''} ${athlete.name || ''}`.trim() })) || []
  ), [data]);

  const handleSaving = useCallback(async (e) => {
    if (!e.changes?.length) {
      return;
    }

    if (!accessToken) {
      notify('Kein Zugriffstoken verfügbar. Bitte erneut anmelden.', 'warning', 3000);
      e.cancel = true;
      return;
    }

    e.cancel = true;
    setIsSaving(true);

    try {
      for (const change of e.changes) {
        if (change.type === 'insert') {
          const payload = { ...change.data, competitionId };
          const saved = await competitionEntityApi.results.insert(competitionId, payload, accessToken);
          onEntityChange?.('Result', 'insert', saved);
        }

        if (change.type === 'update') {
          const current = storeItems.find((item) => item.resultId === change.key) || {};
          const payload = { ...current, ...change.data, resultId: change.key, competitionId };
          const saved = await competitionEntityApi.results.update(competitionId, change.key, payload, accessToken);
          onEntityChange?.('Result', 'update', saved);
        }

        if (change.type === 'remove') {
          await competitionEntityApi.results.remove(competitionId, change.key, accessToken);
          onEntityChange?.('Result', 'delete', { resultId: change.key });
        }
      }

      e.component.cancelEditData();
    } catch (err) {
      const message = err?.response?.data || err?.message || 'Änderung konnte nicht gespeichert werden';
      notify(message, 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, data, onEntityChange]);

  return (
    <div className='competition-section' style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className='section-header'>
        <div>
          <div className='section-title'>Ergebnisse</div>
          <div className='section-subtitle'>Import, Kontrolle und Veröffentlichung</div>
        </div>
        <div className='section-pill subtle success'>Ergebnispflege</div>
      </div>

      <div className='stats-grid'>
        <div className='stat-card'>
          <div className='stat-number'>{mergedStats.heats}</div>
          <div className='stat-label'>Läufe</div>
        </div>
        <div className='stat-card'>
          <div className='stat-number'>{mergedStats.finished}</div>
          <div className='stat-label'>fertig</div>
        </div>
        <div className='stat-card'>
          <div className='stat-number'>{mergedStats.records}</div>
          <div className='stat-label'>Rekorde</div>
        </div>
        <div className='stat-card'>
          <div className='stat-number'>{mergedStats.relays}</div>
          <div className='stat-label'>Staffeln</div>
        </div>
      </div>

      <div className='action-row'>
        <Button text='Ergebnisse importieren' icon='download' type='normal' stylingMode='outlined' onClick={onImportResults} />
        <Button text='Zwischenstand anzeigen' icon='chart' type='text' disabled />
      </div>

      <div className='section-header' style={{ marginTop: 24 }}>
        <div>
          <div className='section-title'>Resultate</div>
          <div className='section-subtitle'>Bearbeiten, korrigieren, löschen</div>
        </div>
      </div>

      <DataGrid
        className='grid theme-dependent'
        dataSource={dataSource}
        keyExpr='resultId'
        columnAutoWidth
        rowAlternationEnabled
        hoverStateEnabled
        showBorders={false}
        repaintChangesOnly
        onSaving={handleSaving}
        loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
        style={{ flex: 1 }}
      >
        <SearchPanel visible placeholder='Suche nach Athlet, Lauf...' />
        <Column
          dataField='heatId'
          caption='Lauf'
          width={140}
          lookup={{ dataSource: heatLookup, valueExpr: 'id', displayExpr: 'name' }}
        />
        <Column
          dataField='athleteId'
          caption='Athlet'
          width={200}
          lookup={{ dataSource: athleteLookup, valueExpr: 'id', displayExpr: 'name' }}
        />
        <Column dataField='lane' caption='Bahn' width={80} />
        <Column dataField='rank' caption='Platz' width={80} />
        <Column dataField='swimtime' caption='Zeit' width={110} />
        <Column dataField='reactiontime' caption='Reaktionszeit' width={130} />
        <Column dataField='status' caption='Status' width={110} />
        <Column dataField='comment' caption='Kommentar' width={200} />
        <Editing
          mode='popup'
          allowAdding
          allowUpdating
          allowDeleting
          useIcons
        />
        <Scrolling mode='virtual' />
      </DataGrid>
    </div>
  );
}
